import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Suspense, type ReactNode } from "react";
import './components/General.css';
import { MenuProvider } from "./context/MenuProvider";
import { CategoriesProvider } from "./context/CategoriesProvider";
import { PackageProvider } from "./context/PackageProvider";
import MainLayout from "./layouts/MainLayout";
import Unauthorized from "./helper/Unauthorized";
import ProtectedRoute from "./helper/ProtectedRoute";
import NotFound from "./helper/NotFound";
import { BrandingProvider } from "./context/BrandingContext";
import { AuthPortalProvider } from "./context/AuthPortalContext";
import { ProfileProvider } from "./context/ProfileContext";
import { useProfile } from "./context/ProfileContext";
import { TourProvider } from "./context/TourContext";
import AutoLogoutTimer from "../src/helper/AutoLogoutTimer";
import PromotionClaimer from "./components/PromotionClaimer";
import { hasStoredIdentity, hasUnlockedVault } from "./helper/APIClient";
import MaintenancePage from "./components/auth/MaintenancePage";
import { useAuthPortal } from "./context/AuthPortalContext";
import { isMaintenanceBypassActive } from "./helper/maintenanceBypass";
import FullPageLoader from "./helper/FullPageLoader";
import { lazyWithRetry } from "./helper/lazyWithRetry";

const Login = lazyWithRetry("lazy:Login", () => import("./components/LoginForm"));
const UnlockVault = lazyWithRetry("lazy:UnlockVault", () => import("./components/UnlockVaultForm"));
const SignUp = lazyWithRetry("lazy:SignUp", () => import("./components/SignUpForm"));
const LogOut = lazyWithRetry("lazy:LogOut", () => import("./components/LogOut"));
const DashBoard = lazyWithRetry("lazy:DashBoard", () => import("./components/Dashboard"));
const DataCard = lazyWithRetry("lazy:DataCard", () => import("./components/DataCard"));
const CardView = lazyWithRetry("lazy:CardView", () => import("./components/CardView"));
const CardManager = lazyWithRetry("lazy:CardManager", () => import("./components/CardManager"));
const FieldCard = lazyWithRetry("lazy:FieldCard", () => import("./components/FieldCard"));
const UserManagement = lazyWithRetry("lazy:UserManagement", () => import("./components/UserManagement"));
const ProbateManager = lazyWithRetry("lazy:ProbateManager", () => import("./components/ProbateManager"));
const PricePage = lazyWithRetry("lazy:PricePage", () => import("./components/PricingPage"));
const VaultAdmin = lazyWithRetry("lazy:VaultAdmin", () => import("./components/VaultAdmin"));
const ProfileAdministration = lazyWithRetry("lazy:ProfileAdministration", () => import("./components/ProfileAdministration"));
const ProbateExports = lazyWithRetry("lazy:ProbateExports", () => import("./components/ProbateExports"));
const EventLogViewer = lazyWithRetry("lazy:EventLogViewer", () => import("./components/EventLogViewer"));
const RemindersPage = lazyWithRetry("lazy:RemindersPage", () => import("./components/RemindersPage"));
const ExecutorManagement = lazyWithRetry("lazy:ExecutorManagement", () => import("./components/ExecutorManagement"));
const ExecutorPortal = lazyWithRetry("lazy:ExecutorPortal", () => import("./components/ExecutorPortal"));
const EmailManagement = lazyWithRetry("lazy:EmailManagement", () => import("./components/EmailManagement"));
const NurtureManagement = lazyWithRetry("lazy:NurtureManagement", () => import("./components/NurtureManagement"));
const InvoiceManagement = lazyWithRetry("lazy:InvoiceManagement", () => import("./components/InvoiceManagement"));
const MenuManagement = lazyWithRetry("lazy:MenuManagement", () => import("./components/MenuManagement"));
const TakeOnQuestionManagement = lazyWithRetry("lazy:TakeOnQuestionManagement", () => import("./components/TakeOnQuestionManagement"));
const PartnerRecruitment = lazyWithRetry("lazy:PartnerRecruitment", () => import("./components/PartnerRecruitment"));
const ReferralPartnerManagement = lazyWithRetry("lazy:ReferralPartnerManagement", () => import("./components/ReferralPartnerManagement"));
const PartnerProgrammeRules = lazyWithRetry("lazy:PartnerProgrammeRules", () => import("./components/PartnerProgrammeRules"));
const ContentCatalogManagement = lazyWithRetry("lazy:ContentCatalogManagement", () => import("./components/ContentCatalogManagement"));
const PortalManagement = lazyWithRetry("lazy:PortalManagement", () => import("./components/PortalManagement"));
const DocumentProcessingAdmin = lazyWithRetry("lazy:DocumentProcessingAdmin", () => import("./components/DocumentProcessingAdmin"));
const ReadinessAdminConsole = lazyWithRetry("lazy:ReadinessAdminConsole", () => import("./components/ReadinessAdminConsole"));
const ReadinessActionPreviewRedirect = lazyWithRetry("lazy:ReadinessActionPreviewRedirect", () => import("./components/ReadinessActionPreviewRedirect"));
const SharesManagement = lazyWithRetry("lazy:SharesManagement", () => import("./components/SharesManagement"));
const ActivityPage = lazyWithRetry("lazy:ActivityPage", () => import("./components/ActivityPage"));
const MandateChangeCompletePage = lazyWithRetry("lazy:MandateChangeCompletePage", () => import("./components/MandateChangeCompletePage"));
const DirectDebitSetupCompletePage = lazyWithRetry("lazy:DirectDebitSetupCompletePage", () => import("./components/DirectDebitSetupCompletePage"));
const ReferralPartnerPortal = lazyWithRetry("lazy:ReferralPartnerPortal", () => import("./components/ReferralPartnerPortal"));
const PartnerMarketplacePortal = lazyWithRetry("lazy:PartnerMarketplacePortal", () => import("./components/PartnerMarketplacePortal"));
const MarketplaceDirectory = lazyWithRetry("lazy:MarketplaceDirectory", () => import("./components/MarketplaceDirectory"));
const MarketplacePartnerProfile = lazyWithRetry("lazy:MarketplacePartnerProfile", () => import("./components/MarketplacePartnerProfile"));
const MarketplaceAdmin = lazyWithRetry("lazy:MarketplaceAdmin", () => import("./components/MarketplaceAdmin"));
const DocumentProcessingFlow = lazyWithRetry("lazy:DocumentProcessingFlow", () => import("./components/DocumentProcessingFlow"));
const SystemStatusAdmin = lazyWithRetry("lazy:SystemStatusAdmin", () => import("./components/SystemStatusAdmin"));
const AdminStats = lazyWithRetry("lazy:AdminStats", () => import("./components/AdminStats"));
const PromotionsAdmin = lazyWithRetry("lazy:PromotionsAdmin", () => import("./components/PromotionsAdmin"));
const ProbateAdministration = lazyWithRetry("lazy:ProbateAdministration", () => import("./components/ProbateAdministration"));
const TestingPage = lazyWithRetry("lazy:TestingPage", () => import("./components/TestingPage"));
const EssentialStorageInfoPage = lazyWithRetry("lazy:EssentialStorageInfoPage", () => import("./components/EssentialStorageInfoPage"));
const TakeOnFlow = lazyWithRetry("lazy:TakeOnFlow", () => import("./components/TakeOnFlow"));
const TakeOnCompletePage = lazyWithRetry("lazy:TakeOnCompletePage", () => import("./components/TakeOnCompletePage"));
const TakeOnFirstWinPage = lazyWithRetry("lazy:TakeOnFirstWinPage", () => import("./components/TakeOnFirstWinPage"));
const TakeOnAlternativePage = lazyWithRetry("lazy:TakeOnAlternativePage", () => import("./components/TakeOnAlternativePage"));
const RestartOnboardingPage = lazyWithRetry("lazy:RestartOnboardingPage", () => import("./components/RestartOnboardingPage"));
const MilestoneHistoryPage = lazyWithRetry("lazy:MilestoneHistoryPage", () => import("./components/readiness/MilestoneHistoryPage"));

const RouteFallback = () => <div className="root-page app-route-page">Loading...</div>;

const AuthRouteGate = ({ children }: { children: ReactNode }) => {
  const { maintenance } = useAuthPortal();

  if (maintenance && !isMaintenanceBypassActive()) {
    return <MaintenancePage title={maintenance.title} message={maintenance.message} />;
  }

  return <>{children}</>;
};

const AuthenticatedProviders = () => {
  const location = useLocation();

  if (!hasStoredIdentity()) {
    return <Navigate to="/login" replace />;
  }

  if (!hasUnlockedVault()) {
    return (
      <Navigate
        to="/unlock"
        replace
        state={{ returnTo: `${location.pathname}${location.search}` }}
      />
    );
  }

  return (
    <AuthPortalProvider>
      <BrandingProvider>
        <ProfileProvider>
          <Outlet />
        </ProfileProvider>
      </BrandingProvider>
    </AuthPortalProvider>
  );
};

const TakeOnGate = () => {
  const { profile, loading } = useProfile();
  const location = useLocation();
  const takeOnReturnPath =
    typeof (location.state as { returnToPath?: string } | null)?.returnToPath === "string"
      ? (location.state as { returnToPath?: string }).returnToPath?.trim()
      : "";

  if (loading) {
    return <FullPageLoader />;
  }

  const isTakeOnRoute = location.pathname === "/take-on";
  const isTakeOnFirstWinRoute = location.pathname === "/take-on/first-win";
  const isTakeOnAlternativeRoute = location.pathname === "/take-on/first-win/alternatives";
  const isTakeOnCompletionRoute = location.pathname === "/take-on/complete";
  const isTakeOnCardsViewRoute = location.pathname === "/take-on/cards-view";
  const isTakeOnCardDetailsRoute = location.pathname === "/take-on/card-details";
  const isTakeOnDocumentRoute = location.pathname === "/document-processing" && Boolean((location.state as { takeOnMode?: boolean } | null)?.takeOnMode);
  const takeOnRequired = profile?.takeOnRequired === true;
  const isTakeOnContinuationRoute = (isTakeOnCardsViewRoute || isTakeOnCardDetailsRoute) && takeOnReturnPath === "/take-on/complete";

  const isAllowedTakeOnRoute =
    isTakeOnRoute ||
    isTakeOnFirstWinRoute ||
    isTakeOnAlternativeRoute ||
    isTakeOnCardsViewRoute ||
    isTakeOnCardDetailsRoute ||
    isTakeOnDocumentRoute;

  if (takeOnRequired && !isAllowedTakeOnRoute) {
    return <Navigate to="/take-on" replace />;
  }

  // Skipping the initial induction completes the session while this route is still
  // mounted. Go straight to the portal instead of inserting the completion screens.
  if (!takeOnRequired && isTakeOnRoute) {
    return <Navigate to="/" replace />;
  }

  if (!takeOnRequired && (isTakeOnFirstWinRoute || isTakeOnAlternativeRoute)) {
    return <Navigate to="/take-on/complete" replace />;
  }

  if (!takeOnRequired && (isTakeOnCardsViewRoute || isTakeOnCardDetailsRoute) && !isTakeOnContinuationRoute) {
    return <Navigate to="/take-on/complete" replace />;
  }

  if (takeOnRequired && isTakeOnCompletionRoute) {
    return <Navigate to="/take-on" replace />;
  }

  return <Outlet />;
};

const AuthenticatedMainLayout = () => (
  <TourProvider>
    <MenuProvider>
      <CategoriesProvider>
        <PackageProvider>
          <PromotionClaimer />
          <MainLayout />
        </PackageProvider>
      </CategoriesProvider>
    </MenuProvider>
  </TourProvider>
);

const AuthenticatedAppProviders = () => (
  <TourProvider>
    <MenuProvider>
      <CategoriesProvider>
        <PackageProvider>
          <Outlet />
        </PackageProvider>
      </CategoriesProvider>
    </MenuProvider>
  </TourProvider>
);

function App() {
  return (
    <BrowserRouter>
      <AutoLogoutTimer />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
        <Route
          path="login"
          element={
            <AuthPortalProvider>
              <AuthRouteGate>
                <Login />
              </AuthRouteGate>
            </AuthPortalProvider>
          }
        />
        <Route
          path="unlock"
          element={
            <AuthPortalProvider>
              <AuthRouteGate>
                <UnlockVault />
              </AuthRouteGate>
            </AuthPortalProvider>
          }
        />
        <Route
          path="signup"
          element={
            <AuthPortalProvider>
              <AuthRouteGate>
                <SignUp mode="signup" />
              </AuthRouteGate>
            </AuthPortalProvider>
          }
        />
        <Route
          path="partner-application"
          element={
            <AuthPortalProvider>
              <AuthRouteGate>
                <SignUp mode="partnerApplication" />
              </AuthRouteGate>
            </AuthPortalProvider>
          }
        />
        <Route
          path="maintenance"
          element={
            <MaintenancePage message="FamilySafe is currently unavailable while we carry out essential work on the platform." />
          }
        />
        <Route path="essential-storage" element={<EssentialStorageInfoPage />} />
        <Route
          path="/logout"
          element={
            <AuthPortalProvider>
              <LogOut />
            </AuthPortalProvider>
          }
        />
        <Route path="/billing/mandate-change/complete" element={<MandateChangeCompletePage />} />
        <Route
          path="/billing/direct-debit/complete"
          element={
            <AuthPortalProvider>
              <BrandingProvider>
                <ProfileProvider>
                  <PackageProvider>
                    <DirectDebitSetupCompletePage />
                  </PackageProvider>
                </ProfileProvider>
              </BrandingProvider>
            </AuthPortalProvider>
          }
        />
        <Route element={<AuthenticatedProviders />}>
          <Route element={<TakeOnGate />}>
            <Route path="restart-onboarding" element={<RestartOnboardingPage />} />
            <Route path="take-on" element={<TakeOnFlow />} />
            <Route path="take-on/first-win" element={<TakeOnFirstWinPage />} />
            <Route path="take-on/first-win/alternatives" element={<TakeOnAlternativePage />} />
            <Route path="take-on/complete" element={<TakeOnCompletePage />} />
            <Route element={<AuthenticatedAppProviders />}>
              <Route element={<ProtectedRoute path="/cards-view" />}>
                <Route path="take-on/cards-view" element={<CardView />} />
              </Route>
              <Route element={<ProtectedRoute path="/card-details" />}>
                <Route path="take-on/card-details" element={<DataCard />} />
              </Route>
            </Route>
            <Route element={<AuthenticatedMainLayout />}>
              <Route path="/" element={<DashBoard />} />
              <Route path="readiness/milestones" element={<MilestoneHistoryPage />} />

              <Route element={<ProtectedRoute path="/cards-view" />}>
                <Route path="cards-view" element={<CardView />} />
              </Route>

              <Route element={<ProtectedRoute path="/card-details" />}>
                <Route path="card-details" element={<DataCard />} />
              </Route>

              <Route element={<ProtectedRoute path="/document-processing" />}>
                <Route path="document-processing" element={<DocumentProcessingFlow />} />
              </Route>

              <Route element={<ProtectedRoute path="/field-card" />}>
                <Route path="field-card" element={<FieldCard />} />
              </Route>

              <Route element={<ProtectedRoute path="/card-manager" />}>
                <Route path="card-manager" element={<CardManager />} />
              </Route>
          
              <Route element={<ProtectedRoute path="/probate-manager" />}>
                <Route path="probate-manager" element={<ProbateManager />} />
              </Route>
          
              <Route element={<ProtectedRoute path="/probate-exports" />}>
                <Route path="probate-exports" element={<ProbateExports />} />
              </Route>

              <Route element={<ProtectedRoute path="/user-list" />}>
                <Route path="user-list" element={<UserManagement />} />
              </Route>

              <Route element={<ProtectedRoute path="/vault-administration" />}>
                <Route path="vault-administration" element={<VaultAdmin />} />
              </Route>

              <Route element={<ProtectedRoute path="/event-log" />}>
                <Route path="event-log" element={<EventLogViewer />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/email-management" />}>
                <Route path="admin/email-management" element={<EmailManagement />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/communications/nurture" />}>
                <Route path="admin/communications/nurture" element={<NurtureManagement />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/invoices" />}>
                <Route path="admin/invoices" element={<InvoiceManagement />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/menu-management" />}>
                <Route path="admin/menu-management" element={<MenuManagement />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/take-on-questions" />}>
                <Route path="admin/take-on-questions" element={<TakeOnQuestionManagement />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/partners" />}>
                <Route path="admin/partners" element={<Navigate to="/admin/referral-partners" replace />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/partners/recruitment" />}>
                <Route path="admin/partners/recruitment" element={<PartnerRecruitment />} />
                <Route path="admin/partners/recruitment/:section" element={<PartnerRecruitment />} />
                <Route path="admin/partners/recruitment/prospects/:prospectId" element={<PartnerRecruitment />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/referral-partners" />}>
                <Route path="admin/referral-partners" element={<ReferralPartnerManagement />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/partner-programmes" />}>
                <Route path="admin/partner-programmes" element={<PartnerProgrammeRules />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/content-catalog" />}>
                <Route path="admin/content-catalog" element={<ContentCatalogManagement />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/portal-management" />}>
                <Route path="admin/portal-management" element={<PortalManagement />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/document-processing" />}>
                <Route path="admin/document-processing" element={<DocumentProcessingAdmin />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/system-status" />}>
                <Route path="admin/system-status" element={<SystemStatusAdmin />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/stats" />}>
                <Route path="admin/stats" element={<AdminStats />} />
              </Route>
              <Route element={<ProtectedRoute path="/admin/promotions" />}>
                <Route path="admin/promotions" element={<PromotionsAdmin />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/probate/death-reports" />}>
                <Route path="admin/probate/death-reports" element={<ProbateAdministration />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/testing" />}>
                <Route path="admin/testing" element={<TestingPage />} />
              </Route>

              <Route element={<ProtectedRoute path="/readiness-admin" />}>
                <Route path="readiness-admin" element={<ReadinessAdminConsole />} />
                <Route path="readiness-action-preview" element={<ReadinessActionPreviewRedirect />} />
              </Route>

              <Route element={<ProtectedRoute path="/reminders" />}>
                <Route path="reminders" element={<RemindersPage />} />
              </Route>

              <Route element={<ProtectedRoute path="/executor-management" />}>
                <Route path="executor-management" element={<ExecutorManagement />} />
              </Route>

              <Route element={<ProtectedRoute path="/executor-portal" />}>
                <Route path="executor-portal" element={<ExecutorPortal />} />
              </Route>

              <Route element={<ProtectedRoute path="/shares" />}>
                <Route path="shares" element={<SharesManagement />} />
              </Route>

              <Route element={<ProtectedRoute path="/activity" />}>
                <Route path="activity" element={<ActivityPage />} />
              </Route>

              <Route element={<ProtectedRoute path="/partner" />}>
                <Route path="partner" element={<ReferralPartnerPortal />} />
              </Route>

              <Route element={<ProtectedRoute path="/partner/marketplace" />}>
                <Route path="partner/marketplace" element={<PartnerMarketplacePortal />} />
              </Route>

              <Route element={<ProtectedRoute path="/marketplace" />}>
                <Route path="marketplace" element={<MarketplaceDirectory />} />
                <Route path="marketplace/partners/:profileId" element={<MarketplacePartnerProfile />} />
              </Route>

              <Route element={<ProtectedRoute path="/admin/marketplace" />}>
                <Route path="admin/marketplace" element={<MarketplaceAdmin />} />
              </Route>

              <Route path="profile-administration" element={<ProfileAdministration />} />
              <Route path="profile-admin" element={<ProfileAdministration />} />

              {/* <Route element={<ProtectedRoute path="/ReferralPartnerPortal" />}>
              <Route path="partner" element={<ReferralPartnerPortal />} />
              </Route> */}

              <Route element={<ProtectedRoute path="/price" />}>
                <Route path="subscriptions" element={<PricePage />} />
                <Route path="price" element={<Navigate to="/subscriptions" replace />} />
              </Route>

            </Route>
          </Route>
        </Route>

        <Route path="/unauthorized" element={<Unauthorized />} />

        <Route path="*" element={<NotFound />} />

        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;

